/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import React from 'react';
import type { ReferenceExpression } from '../../../types/decorator-expression';
import { referenceIsBehind, withReferenceSeenAt } from './shared-expression-version';

/**
 * **Looking at a changed shared expression is the revisit** (`#1302`) — recorded by whatever shows it.
 *
 * It lived only in `SharedExpressionPanel`, which mounts when the `from <name>` chip is clicked. So
 * opening the **row** — the act the `changed` mark on the collapsed row invites — recorded nothing,
 * and the mark survived opening, saving and retesting (Todd, validating `#1315`, 2026-09-14). The
 * panel's own test mounted the panel directly, which is the one path that worked.
 *
 * Now the expanded value line calls this too, so opening the row is enough. The write is a record of
 * attention rather than an edit: it is excluded from unsaved work (`withoutRecordedVersions`), and
 * the mark clears for good when the operator next saves.
 *
 * **Only while the row is behind**, so opening an unchanged row writes nothing, and it cannot loop:
 * the write makes `behind` false. A row that recorded **nothing** is not stamped — absence is *nobody
 * recorded a version*, and inventing a baseline on a glance would start marking from a moment the
 * author did not choose.
 *
 * **The dependencies are the two facts, not the two objects.** `onChange` and `reference` are new on
 * every render, and a caller that does not feed the change back — every render-and-capture harness —
 * would otherwise write without end.
 */
export function useRecordRevisit(
  reference: ReferenceExpression,
  version: string | undefined,
  onChange: (next: ReferenceExpression) => void,
): void {
  const behind = referenceIsBehind(reference, version);
  React.useEffect(() => {
    if (behind && version) onChange(withReferenceSeenAt(reference, version));
  }, [behind, version]);
}
